import { selectLastOpenedExternalStream } from 'features/layout/features/order/orderSlice';
import { selectVideoMidByFeedId } from 'features/streaming/streamingSlice';
import { selectGridPanel } from 'features/layout/features/content/contentSlice';
import { useSimulcast } from 'hooks/useSimulcast';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';

const QUALITY_LABEL_KEYS: Record<number, Record<number, string>> = {
  2: {
    0: 'grid.simulcast_layer_low',
    1: 'grid.simulcast_layer_high',
  },
  3: {
    0: 'grid.simulcast_layer_low',
    1: 'grid.simulcast_layer_medium',
    2: 'grid.simulcast_layer_high',
  },
};

export const useSimulcastLayerControl = () => {
  const { t } = useTranslation('room');

  const activePanel = useAppSelector(selectGridPanel);
  const source = useAppSelector(selectLastOpenedExternalStream);
  const videoMid = useAppSelector((state) => selectVideoMidByFeedId(state, source?.feedId));

  const layerCount = source?.layerCount ?? 0;
  const controlEnabled = Boolean(videoMid) && layerCount > 1 && activePanel === 'external';

  const { substream, changeSubstream } = useSimulcast(videoMid ?? '');

  const [pendingLayer, setPendingLayer] = useState<number | null>(null);

  useEffect(() => {
    if (pendingLayer !== null && substream === pendingLayer) {
      setPendingLayer(null);
    }
  }, [substream, pendingLayer]);

  const activeLayer = pendingLayer ?? substream ?? layerCount - 1;

  // High → low so the menu reads top-down by quality.
  const layers = useMemo(
    () => Array.from({ length: layerCount }, (_, i) => layerCount - 1 - i),
    [layerCount]
  );

  const getLayerLabel = (index: number) => {
    const key = QUALITY_LABEL_KEYS[layerCount]?.[index];
    return key ? t(key) : t('grid.simulcast_layer_n', { layer: index });
  };

  const selectLayer = (index: number) => {
    setPendingLayer(index);
    changeSubstream({} as React.MouseEvent<HTMLElement>, index);
  };

  return {
    controlEnabled,
    layers,
    activeLayer,
    activeLayerLabel: getLayerLabel(activeLayer),
    isPending: pendingLayer !== null,
    selectLayer,
    getLayerLabel,
    tooltip: t('grid.simulcast_layer'),
    icon: 'filter',
  };
};
